import "./ConfirmRdvPoint.scss";
import React, {useState, useMemo} from "react";
import {
    GoogleMap,
    Marker,
    useJsApiLoader,
} from "@react-google-maps/api";
import {
    IonContent,
    IonButton,
    IonLabel,
    IonInput,
    IonText, IonIcon,
} from "@ionic/react";
import {useHistory} from "react-router-dom";
import {getDistanceMeters} from "../../../assets/utils/distance";
import {Libraries} from "@react-google-maps/api/dist/utils/make-load-script-url";
import {RoutePath} from "../../../model/RoutePath.enum";
import {useStateMachine} from "little-state-machine";
import {useGeolocationPermission} from "../../../hooks/useGeolocationPermission";
import {MainFooter, MainHeader} from "../../Layout/Layout";
import {Trans} from "react-i18next";
import {CountryUtils} from "../../../helpers/CountryUtils";
import {
    updateIsAbroadCase,
    updateHasSubmittedLocation,
    updateGeocodedAdditionalInformations,
    updateGeocodedResult,
    updateSteps
} from "../../../state/updateState";
import {useTranslation} from "react-i18next";

const libraries: Libraries = ["places"];

const MAX_DISTANCE_METERS = 200;
const MAP_ZOOM = 14;

export interface LocationFields {
    refresh: boolean; // if true refresh the page (ios doest not load the map when backing from personal details
    address: string;
    additionalInformation?: string;
    location: { lat: number; lng: number } | undefined;
    googleResult: google.maps.GeocoderResult | undefined;
    placeName: string | undefined;
    autoComplete?: string;
    hasUpdatedAddress?: boolean;
}

const ConfirmRdvPoint: React.FC = () => {
    const {t} = useTranslation();
    const history = useHistory();
    const {actions, state} = useStateMachine({
        updateIsAbroadCase,
        updateHasSubmittedLocation,
        updateGeocodedAdditionalInformations,
        updateGeocodedResult,
        updateSteps
    });
    const rdvPoint = state.rdvPoint;
    const detectedPoint = state.detectedPoint;
    const mapOptions: google.maps.MapOptions = {
        streetViewControl: false,
        fullscreenControl: false,
        mapTypeControl: false,
        draggable: false,
        zoomControl: false,
        clickableIcons: false,
        gestureHandling: "cooperative",
        disableDefaultUI: true
    };

    const [comment, setComment] = useState(state.geocodedAdditionalInformations ?? undefined);
    const {isLoaded} = useJsApiLoader({
        id: "google-map-script",
        googleMapsApiKey: process.env.REACT_APP_GOOGLE_API_KEY!,
        libraries
    });

    const distance = useMemo(() => {
        if (!rdvPoint || !detectedPoint) return 0;
        return getDistanceMeters(rdvPoint, detectedPoint);
    }, [rdvPoint, detectedPoint]);

    const requiresComment = useGeolocationPermission() !== "denied" && detectedPoint && distance > MAX_DISTANCE_METERS;
    const isCommentValid = !requiresComment || (comment && comment.trim().length > 0);

    const handleConfirm = () => {
        console.log("Rendez-vous confirmed:", {
            rdvPoint,
            detectedPoint,
            comment,
        });
        // Save and navigate forward
        if (!rdvPoint) return;

        const geocoder = new google.maps.Geocoder();

        geocoder.geocode({location: rdvPoint}, (results, status) => {
            if (status === "OK" && results && results.length > 0) {
                const geocodedResult = results[0];
                actions.updateHasSubmittedLocation(true);
                actions.updateGeocodedResult(geocodedResult);
                actions.updateGeocodedAdditionalInformations(comment);

                // We stored raw informations. Now we must keep datas for legacy.
                const locationFields = {
                    autocomplete: geocodedResult.address_components,
                    address: geocodedResult.formatted_address,
                    location: {
                        lat: geocodedResult.geometry.location.lat(),
                        lng: geocodedResult.geometry.location.lng(),
                    },
                    placeName: "",
                    refresh: true,
                    googleResult: geocodedResult,
                    additionalInformation: comment
                } as LocationFields;
                actions.updateSteps({locationFields});

                console.log("Raw geocode:", geocodedResult);
                console.log(CountryUtils.isInSwitzerlandOrLiechtenstein(geocodedResult))
                if (CountryUtils.isInSwitzerlandOrLiechtenstein(geocodedResult)) {
                    actions.updateIsAbroadCase(false);
                    history.push(`${RoutePath.REQUEST}/${RoutePath.PERSONAL_DETAILS}`, {
                        from: RoutePath.CONFIRM_RDV_POINT,
                    });
                } else {
                    actions.updateIsAbroadCase(true);
                    history.push(`${RoutePath.REQUEST}/${RoutePath.ABROAD_ADDITIONAL_INFOS}`, {
                        from: RoutePath.CONFIRM_RDV_POINT,
                    });
                }
            } else {
                console.error("Geocoder failed due to:", status);
            }
        });
    };

    return (
        <IonContent className="confirmRDVContent">
            <MainHeader
                title={t("confirmRdvPoint_header")}
                image={`${process.env.PUBLIC_URL}/assets/images/${
                    process.env.REACT_APP_ALT_ICONS_PATH || ""
                }Pin.svg`}
            >
                <div className="info-tip">
                    <IonIcon 
                        src={`${process.env.PUBLIC_URL}/assets/images/information-circle.svg`}
                    />
                    <IonText>
                        {t("confirmRdvPoint_tapOnTheMap")}
                    </IonText>
                </div>
                <div className="map-container">
                    {isLoaded && rdvPoint && (
                        <GoogleMap
                            center={rdvPoint}
                            zoom={MAP_ZOOM}
                            mapContainerClassName="map-element"
                            onClick={() => history.push(`${RoutePath.REQUEST}/${RoutePath.MANUAL_LOCATION}`)}
                            options={mapOptions}
                        >
                            <Marker position={rdvPoint}/>
                            {detectedPoint && (
                                <Marker
                                    position={detectedPoint}
                                    icon={{
                                        path: google.maps.SymbolPath.CIRCLE,
                                        scale: 8,
                                        fillColor: "#4285F4",
                                        fillOpacity: 1,
                                        strokeWeight: 2,
                                        strokeColor: "#ffffff",
                                    }}
                                />
                            )}
                        </GoogleMap>
                    )}
                </div>

                {requiresComment && (
                    <div className="comment-warning">
                        <IonIcon 
                            src={`${process.env.PUBLIC_URL}/assets/images/Warning.svg`}
                        />
                        <IonText>
                            <Trans
                                i18nKey="confirmRdvPoint_detectedTooFar"
                                values={{distance: MAX_DISTANCE_METERS}}
                            >
                                Detected point is more than {{distance: MAX_DISTANCE_METERS}} m from rendez-vous point.
                                Please provide additional information.
                            </Trans>
                        </IonText>
                    </div>
                )}

                <div className="form-group">
                    <IonLabel position="stacked">{t("confirmRdvPoint_additionalInformation")}</IonLabel>
                    <IonInput
                        placeholder={t("confirmRdvPoint_additionalInformationPlaceholder")}
                        value={comment}
                        onIonChange={(e) => setComment(e.detail.value!)}
                    />
                </div>
            </MainHeader>
            <MainFooter>
                <IonButton expand="block"
                           className="button-tcs back"
                           onClick={() => history.back()}
                >
                    <div className="wrapper">
                        <IonIcon
                            size="large"
                            src={`${process.env.PUBLIC_URL}/assets/images/chevron-back.svg`}
                        />
                        <IonLabel>{t("Back")}</IonLabel>
                        <div/>
                    </div>
                </IonButton>
                <IonButton
                    expand="block"
                    className="button-tcs cta"
                    color="primary"
                    onClick={handleConfirm}
                    disabled={!isCommentValid}
                >
                    <div className="wrapper">
                        <div/>
                        <IonLabel>{t("Confirm")}</IonLabel>
                        <IonIcon
                            size="large"
                            src={`${process.env.PUBLIC_URL}/assets/images/chevron-forward.svg`}
                        />
                    </div>
                </IonButton>
            </MainFooter>
        </IonContent>
    );
};

export default ConfirmRdvPoint;
